import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BadgeCheck,
  Crown,
  KeyRound,
  LockKeyhole,
  Mail,
  Plus,
  Settings2,
  ShieldCheck,
  Trash2,
  UserCog,
  UserRoundPlus,
  Users,
  X,
} from 'lucide-react';
import AdminPhone from '../components/AdminPhone';
import './Team.css';
import PageHeader from '../components/PageHeader';

const PERM_LABEL = {
  'partners.delete': 'Partner löschen',
  'drivers.delete': 'Fahrer löschen',
  'admins.manage': 'Zugänge verwalten',
  'finance.manage': 'Abrechnung & Auszahlungen',
  'settings.manage': 'Einstellungen ändern',
  'engine.view': 'Engine nur ansehen',
  'engine.manage': 'Engine & Pricing verwalten',
  'agent.view': 'KI-Agent nur ansehen',
  'agent.manage': 'KI-Agent-Betrieb verwalten',
  'freigaben.view': 'Freigaben ansehen',
  'freigaben.manage': 'Freigaben entscheiden',
  'location_trust.view': 'Standort-Evidenz ansehen',
  'location_trust.manage': 'Standort-Evidenz entscheiden',
  'location_trust.appeal': 'Standort-Einsprüche bearbeiten',
  'location_trust.config': 'Standort-/Attestation-Konfiguration',
  'data.explore': 'Daten-Explorer · read-only',
};

const EMPTY_FORM = { name: '', email: '', password: '', permissions: [] };

function Metric({ icon: Icon, label, value, detail, tone = 'neutral' }) {
  return (
    <article className={`team-metric is-${tone}`}>
      <div className="team-metric-icon"><Icon size={19} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function initials(name) {
  return String(name || 'A')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

export default function Team() {
  const [admins, setAdmins] = useState([]);
  const [perms, setPerms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get('/admin/admins').then(response => {
    setAdmins(response.data.admins || []);
    setPerms(response.data.available_perms || []);
    setRoles(response.data.available_roles || []);
  });

  useEffect(() => {
    load();
  }, []);

  const applyRoleNew = role => {
    setForm(current => ({ ...current, permissions: [...role.perms] }));
  };

  const applyRole = async (admin, role) => {
    try {
      await api.patch(`/admin/admins/${admin.id}`, { permissions: role.perms });
      toast.success(`Rolle „${role.label}" gesetzt`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const toggleNew = permission => {
    setForm(current => ({
      ...current,
      permissions: current.permissions.includes(permission)
        ? current.permissions.filter(item => item !== permission)
        : [...current.permissions, permission],
    }));
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, E-Mail und Passwort sind nötig');
      return;
    }
    try {
      await api.post('/admin/admins', form);
      toast.success('Zugang angelegt');
      setShowNew(false);
      setForm(EMPTY_FORM);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const togglePerm = async (admin, permission) => {
    const next = admin.permissions.includes(permission)
      ? admin.permissions.filter(item => item !== permission)
      : [...admin.permissions, permission];
    try {
      await api.patch(`/admin/admins/${admin.id}`, { permissions: next });
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const remove = async admin => {
    if (!window.confirm(`Zugang „${admin.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/admins/${admin.id}`);
      toast.success('Zugang gelöscht');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const protectedCount = admins.filter(admin => admin.full || admin.protected).length;
  const delegatedCount = admins.length - protectedCount;

  return (
    <div className="team-page">
      <PageHeader
        title="Team &amp; Rechte"
        description="Admin-Zugänge, Rollen und kritische Berechtigungen für den VOOGO Betrieb – mit klarer Eigentümer- und Voll-Admin-Grenze."
        actions={
        <button
          type="button"
          className={`team-create-toggle ${showNew ? 'is-open' : ''}`}
          onClick={() => setShowNew(value => !value)}
        >
          {showNew ? <X size={16} /> : <Plus size={16} />}
          {showNew ? 'Einrichtung schließen' : 'Zugang anlegen'}
        </button>
      }
      />

      <section className="team-boundary" aria-label="Zugriffsgrenze">
        <div className="team-boundary-icon"><LockKeyhole size={23} /></div>
        <div>
          <span>Protected identity boundary</span>
          <h2>Eigentümer- und Voll-Admin-Konten bleiben gesperrt</h2>
          <p>Die Oberfläche vergibt ausschließlich die serverseitig erlaubten Rollen und Einzelrechte. Vollzugriff <code>*</code> wird hier nie vergeben.</p>
        </div>
        <div className="team-boundary-status">
          <span><i /> Least Privilege</span>
          <small>{protectedCount} geschützte Konten</small>
        </div>
      </section>

      <section className="team-metrics" aria-label="Zugriffskennzahlen">
        <Metric icon={Users} label="Admin-Zugänge" value={admins.length} detail="VOOGO Operator-Konten" tone="purple" />
        <Metric icon={UserCog} label="Delegiert" value={delegatedCount} detail="rollenbasiert verwaltbar" />
        <Metric icon={Settings2} label="Rollenvorlagen" value={roles.length} detail="Schnellzuweisungen" />
        <Metric icon={KeyRound} label="Einzelrechte" value={perms.length} detail="serverseitig erlaubt" tone="green" />
      </section>

      <section className="team-phone-section">
        <div className="team-section-heading">
          <div>
            <span>01 · Eigene Sicherheit</span>
            <h2>Wiederherstellungsnummer</h2>
            <p>Jeder Admin verwaltet ausschließlich die Nummer des eigenen eingeloggten Kontos.</p>
          </div>
          <div className="team-sms-pill"><BadgeCheck size={14} /> Änderung per Code</div>
        </div>
        <div className="team-phone-panel">
          <AdminPhone />
        </div>
      </section>

      {showNew && (
        <section className="team-new-access">
          <div className="team-section-heading">
            <div>
              <span>02 · Neuer Zugang</span>
              <h2>Admin-Zugang einrichten</h2>
              <p>Identität erfassen, Rollenvorlage wählen und Einzelrechte vor dem Anlegen prüfen.</p>
            </div>
            <div className="team-new-summary">
              <strong>{form.permissions.length}</strong>
              <small>kritische Rechte gewählt</small>
            </div>
          </div>

          <div className="team-identity-grid">
            <label>
              <span>Name</span>
              <input
                aria-label="Name des neuen Admins"
                value={form.name}
                onChange={event => setForm({ ...form, name: event.target.value })}
                placeholder="Vor- und Nachname"
              />
            </label>
            <label>
              <span>E-Mail</span>
              <input
                aria-label="E-Mail des neuen Admins"
                value={form.email}
                onChange={event => setForm({ ...form, email: event.target.value })}
                placeholder="name@voogo.de"
              />
            </label>
            <label>
              <span>Initiales Passwort</span>
              <input
                aria-label="Passwort des neuen Admins"
                type="password"
                value={form.password}
                onChange={event => setForm({ ...form, password: event.target.value })}
                placeholder="mindestens 10 Zeichen"
              />
            </label>
          </div>

          <div className="team-control-block">
            <div className="team-control-heading">
              <div>
                <span>Rollenvorlage</span>
                <strong>Rechte-Bündel übernehmen</strong>
              </div>
              <small>Eine Vorlage ersetzt die aktuelle Auswahl.</small>
            </div>
            <div className="team-role-list">
              {roles.map(role => (
                <button
                  type="button"
                  className="team-role-template"
                  data-role-key={role.key}
                  key={role.key}
                  onClick={() => applyRoleNew(role)}
                >
                  <ShieldCheck size={14} />
                  <span>{role.label}</span>
                  <small>{role.perms.length} Rechte</small>
                </button>
              ))}
            </div>
          </div>

          <div className="team-control-block">
            <div className="team-control-heading">
              <div>
                <span>Einzelrechte</span>
                <strong>Kritische Berechtigungen feinsteuern</strong>
              </div>
              <small>Allgemeine Ansicht, Bearbeitung und Support bleiben Basiszugriff.</small>
            </div>
            <div className="team-permission-grid">
              {perms.map(permission => {
                const active = form.permissions.includes(permission);
                return (
                  <button
                    type="button"
                    className={`team-permission team-new-permission ${active ? 'is-active' : ''}`}
                    data-permission={permission}
                    aria-pressed={active}
                    key={permission}
                    onClick={() => toggleNew(permission)}
                  >
                    <span>{active ? '✓' : '+'}</span>
                    {PERM_LABEL[permission] || permission}
                  </button>
                );
              })}
            </div>
          </div>

          <footer className="team-new-footer">
            <div>
              <LockKeyhole size={16} />
              <span>Vollzugriff wird nicht über diese Oberfläche vergeben.</span>
            </div>
            <button type="button" className="team-create-submit" onClick={create}>
              <UserRoundPlus size={15} />
              Zugang anlegen
            </button>
          </footer>
        </section>
      )}

      <section className="team-registry">
        <div className="team-section-heading">
          <div>
            <span>03 · Access Registry</span>
            <h2>VOOGO Admin-Team</h2>
            <p>Geschützte Konten sind gesperrt; delegierte Zugänge bleiben rollen- und rechtebasiert steuerbar.</p>
          </div>
          <div className="team-registry-count"><Users size={14} /> {admins.length} Zugänge</div>
        </div>

        <div className="team-admin-list">
          {admins.map(admin => {
            const locked = admin.full || admin.protected;
            return (
              <article
                className={`team-admin-card ${locked ? 'is-locked' : ''}`}
                data-admin-id={admin.id}
                key={admin.id}
              >
                <header className="team-admin-header">
                  <div className="team-admin-identity">
                    <div className={`team-admin-avatar ${admin.full ? 'is-full' : ''}`}>
                      {admin.full ? <Crown size={18} /> : initials(admin.name)}
                    </div>
                    <div>
                      <div className="team-admin-name-row">
                        <h3>{admin.name}</h3>
                        {admin.phone && admin.phone_verified && (
                          <span className="team-verified"><BadgeCheck size={12} /> verifiziert</span>
                        )}
                      </div>
                      <p><Mail size={12} /> {admin.email}</p>
                      <small>{admin.phone || 'keine Telefonnummer hinterlegt'}</small>
                    </div>
                  </div>

                  {locked ? (
                    <div className={`team-lock-badge ${admin.full ? 'is-full' : ''}`}>
                      {admin.full ? <Crown size={14} /> : <LockKeyhole size={14} />}
                      {admin.full ? 'Voll-Admin · geschützt' : 'Eigentümer-Konto · geschützt'}
                    </div>
                  ) : (
                    <button type="button" className="team-delete" onClick={() => remove(admin)}>
                      <Trash2 size={14} />
                      Zugang löschen
                    </button>
                  )}
                </header>

                {locked ? (
                  <div className="team-locked-note">
                    <LockKeyhole size={16} />
                    <div>
                      <strong>Keine Rechteänderung über die Team-Oberfläche</strong>
                      <span>Dieses Konto wird durch die serverseitige Eigentümer-/Voll-Admin-Grenze geschützt.</span>
                    </div>
                  </div>
                ) : (
                  <div className="team-admin-controls">
                    <div className="team-admin-role-row">
                      <span>Rolle setzen</span>
                      <div>
                        {roles.map(role => (
                          <button
                            type="button"
                            className="team-admin-role"
                            data-role-key={role.key}
                            key={role.key}
                            onClick={() => applyRole(admin, role)}
                          >
                            {role.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="team-admin-permissions">
                      <div className="team-admin-permission-heading">
                        <span>Einzelrechte</span>
                        <strong>{admin.permissions.length}/{perms.length} aktiv</strong>
                      </div>
                      <div className="team-permission-grid">
                        {perms.map(permission => {
                          const active = admin.permissions.includes(permission);
                          return (
                            <button
                              type="button"
                              className={`team-permission team-admin-permission ${active ? 'is-active' : ''}`}
                              data-permission={permission}
                              aria-pressed={active}
                              key={permission}
                              onClick={() => togglePerm(admin, permission)}
                            >
                              <span>{active ? '✓' : '+'}</span>
                              {PERM_LABEL[permission] || permission}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
